/**
 * Builds the VaultFS for a connection.
 *
 * This is the only file that knows there is more than one way to reach a vault.
 * Everything above it — the store, the engine, every widget — depends on the
 * `VaultFS` interface alone, which is what made adding GitHub a new file rather
 * than a refactor.
 */

import { HttpVaultFS } from '@hud/adapters/http-vault';
import { GitHubVaultFS } from '@hud/adapters/github-vault';
import type { VaultFS } from '@hud/core';
import type { Connection } from './connection.js';

export const VAULT_API_BASE = '/__vault';

export interface VaultBinding {
  fs: VaultFS;
  /** Vault path → a URL the browser can load directly. */
  assetUrl: (path: string) => string;
  /** Set when blobs need credentials an `<img>` tag cannot send. */
  assetDataUrl?: (path: string) => Promise<string>;
  label: string;
}

export function createVault(connection: Connection): VaultBinding {
  if (connection.kind === 'local') {
    return {
      fs: new HttpVaultFS({ baseUrl: VAULT_API_BASE }),
      assetUrl: (path) => `${VAULT_API_BASE}/raw?path=${encodeURIComponent(path)}`,
      label: 'local folder',
    };
  }

  const fs = new GitHubVaultFS({
    owner: connection.owner,
    repo: connection.repo,
    branch: connection.branch,
    token: connection.token,
    committer: { name: 'HUD', email: 'hud@localhost' },
  });

  return {
    fs,
    // Only correct for public repos; private ones 404 without an auth header,
    // which an <img> cannot send. `assetDataUrl` is the real path — this is
    // just what renders for the instant before it resolves.
    assetUrl: (path) =>
      `https://raw.githubusercontent.com/${connection.owner}/${connection.repo}/${connection.branch}/${path}`,
    assetDataUrl: (path) => fs.assetDataUrl(path),
    label: `${connection.owner}/${connection.repo}`,
  };
}

export { GitHubVaultFS };
